import React from 'react';

function normalizeDefinition(value) {
    return String(value ?? '')
        .trim()
        .replace(/^```(?:mermaid)?\s*/i, '')
        .replace(/```\s*$/i, '')
        .replace(/^mermaid\s*\n/i, '')
        .trim();
}

export default function MermaidDiagram({definition}) {
    const reactId = React.useId();
    const diagramId = React.useMemo(
        () => `knowledge-diagram-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`,
        [reactId],
    );
    const [theme, setTheme] = React.useState(() => document.documentElement.dataset.theme ?? 'dark');
    const [svg, setSvg] = React.useState('');
    const [hasError, setHasError] = React.useState(false);
    const normalizedDefinition = React.useMemo(() => normalizeDefinition(definition), [definition]);

    React.useEffect(() => {
        const observer = new MutationObserver(() => {
            setTheme(document.documentElement.dataset.theme ?? 'dark');
        });
        observer.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme']});
        return () => observer.disconnect();
    }, []);

    React.useEffect(() => {
        let cancelled = false;

        async function renderDiagram() {
            if (!normalizedDefinition) return;

            try {
                const {default: mermaid} = await import('mermaid');
                mermaid.initialize({
                    startOnLoad: false,
                    securityLevel: 'strict',
                    theme: theme === 'light' ? 'neutral' : 'dark',
                    fontFamily: 'inherit',
                });
                const result = await mermaid.render(`${diagramId}-${theme}`, normalizedDefinition);
                if (!cancelled) {
                    setSvg(result.svg);
                    setHasError(false);
                }
            } catch {
                if (!cancelled) {
                    setSvg('');
                    setHasError(true);
                }
            }
        }

        renderDiagram();
        return () => {
            cancelled = true;
        };
    }, [diagramId, normalizedDefinition, theme]);

    if (hasError) {
        return (
            <div className="rounded-md border border-[var(--border-strong)] bg-[var(--surface)] p-3 text-xs text-[var(--text-secondary)]">
                This diagram could not be rendered.
            </div>
        );
    }

    return (
        <div
            role="img"
            aria-label="Lesson diagram"
            className="flex min-h-28 items-center justify-center overflow-x-auto rounded-md border border-[var(--border-strong)] bg-[var(--surface)] p-4 [&_svg]:h-auto [&_svg]:max-w-full"
            dangerouslySetInnerHTML={{__html: svg}}
        />
    );
}
